package com.partejar.app.feature.auth.data.repository

import com.partejar.app.feature.auth.data.dto.AuthRequest
import com.partejar.app.feature.auth.data.dto.AuthResponse
import com.partejar.app.feature.auth.data.dto.RegisterRequest
import com.partejar.app.core.network.ApiConfig
import com.partejar.app.core.network.invalidateSessionTokenCache
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.core.session.SessionTokenStore
import com.partejar.app.core.session.SessionTokens
import com.partejar.app.core.session.SessionLocalStateCleaner
import com.partejar.app.core.session.StoredSessionState
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpStatusCode
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

sealed class RegistrationException : Exception() {
    data object EmailAlreadyRegistered : RegistrationException()
    data object InvalidData : RegistrationException()
    data object Unavailable : RegistrationException()
}

class AccountDeletionPendingLoginException : Exception()

class AuthRepository(
    private val client: HttpClient,
    private val tokenStore: SessionTokenStore,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository,
    private val sessionLocalStateCleaner: SessionLocalStateCleaner? = null
) {

    suspend fun realizarLogin(request: AuthRequest): AuthResponse {
        val httpResponse = client.post(apiConfig.endpoint("/auth/login")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (httpResponse.status == HttpStatusCode.Conflict) {
            val code = runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<LoginErrorResponse>(httpResponse.bodyAsText()).code
            }.getOrNull()
            if (code == "ACCOUNT_DELETION_PENDING") throw AccountDeletionPendingLoginException()
        }
        val response = httpResponse.body<AuthResponse>()

        storeSession(SessionTokens(response.accessToken, response.refreshToken))
        return response
    }

    suspend fun realizarCadastro(request: RegisterRequest): AuthResponse {
        val response = client.post(apiConfig.endpoint("/auth/register")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status != HttpStatusCode.OK) throw response.registrationException()
        return response.body<AuthResponse>().also {
            storeSession(SessionTokens(it.accessToken, it.refreshToken))
        }
    }

    fun storeSession(tokens: SessionTokens) {
        tokenStore.save(tokens)
        client.invalidateSessionTokenCache()
    }

    fun storedSessionState(): StoredSessionState = tokenStore.state()

    fun hasRecoverableSession(): Boolean = tokenStore.current() != null

    suspend fun logout() {
        val refreshToken = tokenStore.current()?.refreshToken
        try {
            if (!refreshToken.isNullOrBlank()) {
                client.post(apiConfig.endpoint("/auth/logout")) {
                    contentType(ContentType.Application.Json)
                    setBody(RefreshTokenLogoutRequest(refreshToken))
                }
            }
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            // A saída local não depende da disponibilidade do servidor.
        } finally {
            clearLocalSession()
        }
    }

    fun clearLocalSession() {
        sessionLocalStateCleaner?.clear() ?: run {
            tokenStore.clear()
            currentWorkspaceRepository.clear()
        }
        client.invalidateSessionTokenCache()
    }
}

@Serializable
private data class RegistrationErrorResponse(val details: String? = null)

@Serializable
private data class LoginErrorResponse(val code: String? = null)

private suspend fun io.ktor.client.statement.HttpResponse.registrationException(): RegistrationException {
    if (status == HttpStatusCode.Conflict) return RegistrationException.EmailAlreadyRegistered
    if (status == HttpStatusCode.BadRequest) {
        val details = runCatching {
            Json { ignoreUnknownKeys = true }.decodeFromString<RegistrationErrorResponse>(bodyAsText()).details
        }.getOrNull().orEmpty()
        // The current public contract has no domain code; details is its structured validation field.
        return if (details.contains("Já existe uma conta", ignoreCase = true)) {
            RegistrationException.EmailAlreadyRegistered
        } else RegistrationException.InvalidData
    }
    return RegistrationException.Unavailable
}

@kotlinx.serialization.Serializable
private data class RefreshTokenLogoutRequest(val refreshToken: String)
